import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles } from "lucide-react";

// Support Hub Stage 5, Part B
// Meeting Observances captures the ceremonial and procedural moments
// that the canon minutes render. All values live under
// meeting_minutes.observances jsonb, except meeting_type which is a top-level column.

interface PriorMinutesApproval {
  prior_date?: string;
  votes_for?: number;
  votes_against?: number;
}

interface Observances {
  invocation_given?: boolean;
  doctrine_reaffirmed?: boolean;
  benediction_given?: boolean;
  financial_report_delivered?: boolean;
  financial_report_office?: string;
  stewardship_assessment_recorded?: boolean;
  prior_minutes_approval?: PriorMinutesApproval;
}

interface ObservancesSectionProps {
  data: {
    meeting_type?: string;
    observances?: Observances;
  };
  onChange: (updates: any) => void;
  primaryOfficerRole?: string;
}

const MEETING_TYPES = [
  "Officer Meeting",
  "General Meeting",
  "Special Meeting",
  "Quarterly Observance",
  "Annual Meeting",
];

export function ObservancesSection({ data, onChange, primaryOfficerRole }: ObservancesSectionProps) {
  const observances: Observances = data.observances || {};
  const prior: PriorMinutesApproval = observances.prior_minutes_approval || {};
  const meetingType = data.meeting_type || "Officer Meeting";

  const updateObservance = (patch: Partial<Observances>) => {
    onChange({ observances: { ...observances, ...patch } });
  };

  const updatePrior = (patch: Partial<PriorMinutesApproval>) => {
    onChange({
      observances: {
        ...observances,
        prior_minutes_approval: { ...prior, ...patch },
      },
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2 flex items-center gap-2">
          <Sparkles className="h-6 w-6" />
          Observances
        </h2>
        <p className="text-muted-foreground">
          Record the ceremonial and procedural elements of this meeting. These lines appear in the canon minutes.
        </p>
      </div>

      <Card className="p-4 space-y-4">
        <h3 className="font-semibold">Meeting type</h3>
        <div className="space-y-2">
          <Label>Type of meeting</Label>
          <Select value={meetingType} onValueChange={(v) => onChange({ meeting_type: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MEETING_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="p-4 space-y-4">
        <h3 className="font-semibold">Ceremonial elements</h3>
        <div className="flex items-center justify-between">
          <Label htmlFor="invocation" className="cursor-pointer">Invocation given</Label>
          <Switch
            id="invocation"
            checked={!!observances.invocation_given}
            onCheckedChange={(v) => updateObservance({ invocation_given: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label htmlFor="doctrine" className="cursor-pointer">Doctrine reaffirmed</Label>
          <Switch
            id="doctrine"
            checked={!!observances.doctrine_reaffirmed}
            onCheckedChange={(v) => updateObservance({ doctrine_reaffirmed: v })}
          />
        </div>
        <div className="flex items-center justify-between">
          <Label htmlFor="benediction" className="cursor-pointer">Benediction given</Label>
          <Switch
            id="benediction"
            checked={!!observances.benediction_given}
            onCheckedChange={(v) => updateObservance({ benediction_given: v })}
          />
        </div>
      </Card>

      <Card className="p-4 space-y-4">
        <h3 className="font-semibold">Reports and assessments</h3>
        <div className="flex items-center justify-between">
          <Label htmlFor="financial" className="cursor-pointer">Financial report delivered</Label>
          <Switch
            id="financial"
            checked={!!observances.financial_report_delivered}
            onCheckedChange={(v) => updateObservance({ financial_report_delivered: v })}
          />
        </div>
        {observances.financial_report_delivered && (
          <div className="space-y-2">
            <Label htmlFor="fin-office">Delivered by (office)</Label>
            <Input
              id="fin-office"
              value={observances.financial_report_office || ""}
              placeholder={primaryOfficerRole || "Secretary Treasurer"}
              onChange={(e) => updateObservance({ financial_report_office: e.target.value })}
            />
          </div>
        )}
        <div className="flex items-center justify-between">
          <Label htmlFor="stewardship" className="cursor-pointer">Stewardship assessment recorded</Label>
          <Switch
            id="stewardship"
            checked={!!observances.stewardship_assessment_recorded}
            onCheckedChange={(v) => updateObservance({ stewardship_assessment_recorded: v })}
          />
        </div>
      </Card>

      <Card className="p-4 space-y-4">
        <h3 className="font-semibold">Prior minutes approval</h3>
        <p className="text-sm text-muted-foreground">
          If this meeting approved the minutes of a prior meeting, record the date and the vote.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="prior-date">Prior meeting date</Label>
            <Input
              id="prior-date"
              type="date"
              value={prior.prior_date || ""}
              onChange={(e) => updatePrior({ prior_date: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="prior-for">Votes for</Label>
            <Input
              id="prior-for"
              type="number"
              min="0"
              value={prior.votes_for ?? ""}
              onChange={(e) => updatePrior({ votes_for: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="prior-against">Votes against</Label>
            <Input
              id="prior-against"
              type="number"
              min="0"
              value={prior.votes_against ?? ""}
              onChange={(e) => updatePrior({ votes_against: parseInt(e.target.value) || 0 })}
            />
          </div>
        </div>
      </Card>
    </div>
  );
}
